import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
} from "react";
import { CustomAccount } from "../lib/interfaces/CustomAccountInterface";

interface CustomAccountContextType {
  customAccount: CustomAccount | null;
  setCustomAccount: (
    account: CustomAccount,
    bannerImg?: string,
    reportImage?: string
  ) => void;
  bannerImg: string; // Added here
}

const CustomAccountContext = createContext<CustomAccountContextType>({
  customAccount: null,
  setCustomAccount: () => {},
  bannerImg: "", // Initialized here
});

export const CustomAccountProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [customAccount, setCustomAccountState] = useState<CustomAccount | null>(
    null
  );
  const [bannerImg, setBannerImg] = useState<string>("");

  useEffect(() => {
    const storedAccount = sessionStorage.getItem("customAccount");
    const storedBannerImg = sessionStorage.getItem("bannerImg");

    if (storedAccount) {
      try {
        const account = JSON.parse(storedAccount);
        setCustomAccountState(account);
      } catch (error) {
        console.error(
          "Failed to parse custom account from sessionStorage:",
          error
        );
      }
    }

    if (storedBannerImg) {
      setBannerImg(storedBannerImg);
    }
  }, []);

  useEffect(() => {
    if (customAccount) {
      sessionStorage.setItem("customAccount", JSON.stringify(customAccount));
    } else {
      sessionStorage.removeItem("customAccount");
    }

    return () => {
      if (bannerImg) {
        URL.revokeObjectURL(bannerImg);
      }
    };
  }, [customAccount]);

  const memoizedBannerImg = useMemo(() => {
    return bannerImg;
  }, [bannerImg]);

  const setCustomAccount = (
    account: CustomAccount,
    bannerImg?: string,
    reportImage?: string
  ) => {
    const updatedAccount = { ...account, bannerImg, reportImage };
    setCustomAccountState(updatedAccount);

    if (bannerImg) {
      setBannerImg(bannerImg);
      sessionStorage.setItem("bannerImg", bannerImg);
    } else {
      setBannerImg("");
      sessionStorage.removeItem("bannerImg");
    }
  };

  return (
    <CustomAccountContext.Provider
      value={{ customAccount, setCustomAccount, bannerImg: memoizedBannerImg }}
    >
      {children}
    </CustomAccountContext.Provider>
  );
};

export const useCustomAccount = () => useContext(CustomAccountContext);
